import type { HTMLAttributes, SVGProps } from "react"

import { tokens } from "@/lib/tokens.generated"

import { cx } from "@uui/utils/cx"

/*
 * The wordmark: "bl" + the Do aperture as the `u` + "eprints", in Space Grotesk
 * 600 — the name's voice, used nowhere else, so the face is loaded here and is
 * deliberately not a theme token.
 *
 * Only the received arc carries Do's green (do.500, the one green event); the
 * signature is the shape, so the green never colours the `u` itself. Below 22px
 * the wordmark is not used: the glyph stands beside the name in Plex Sans 600
 * (docs/design/identity.md).
 */
export type WordmarkSize = 22 | 24 | 26 | 30 | 44 | 96

/** Glyph metrics per word size: [svg width, svg height, vertical-align, side margin]. */
const METRICS: Record<WordmarkSize, [number, number, number, number]> = {
  22: [12.5, 14, -0.36, 0.7],
  24: [13.6, 15.2, -0.39, 0.8],
  26: [14.7, 16.5, -0.42, 0.9],
  30: [17, 19, -0.49, 1],
  44: [24.9, 27.9, -0.71, 1.7],
  96: [54.4, 60.9, -1.56, 3.5],
}

/*
 * The Do glyph on its own: the mark's 16 grid, strokes in the surface's text
 * colour and the arc in do.500, printed whole wherever it is used.
 * Below 22px it stands beside "Blueprints" in Plex Sans 600 in place
 * of the wordmark; as `iconLeading` it leads an action that starts a
 * relationship-changing thing. There is no ink-only cut.
 */
export function DoGlyph({
  viewBox = "0 0 16 16",
  className,
  ...props
}: SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden="true"
      viewBox={viewBox}
      className={cx("shrink-0", className)}
      fill="none"
      strokeWidth="1.7"
      strokeLinecap="round"
      {...props}
    >
      <g stroke="currentColor">
        <circle
          cx="8"
          cy="8.35"
          r="4.4"
          pathLength="100"
          strokeDasharray="50 50"
          strokeDashoffset="0"
        />
        <line x1="12.4" y1="8.35" x2="12.4" y2="4.9" />
        <line x1="3.6" y1="8.35" x2="3.6" y2="4.9" />
      </g>
      <g
        transform="translate(8,3.6) rotate(-160)"
        stroke={tokens.color.do[500]}
      >
        <path d="M2.6 0A2.6 2.6 0 0 1 -2.6 0" />
      </g>
    </svg>
  )
}

export function Wordmark({
  size = 26,
  className,
  ...props
}: {
  size?: WordmarkSize
} & HTMLAttributes<HTMLSpanElement>) {
  const [w, h, valign, margin] = METRICS[size]
  return (
    <span
      role="img"
      aria-label="blueprints"
      className={cx("inline-block whitespace-nowrap", className)}
      style={{
        fontFamily: "'Space Grotesk', 'IBM Plex Sans', sans-serif",
        fontWeight: 600,
        fontSize: size,
        lineHeight: 1,
        letterSpacing: size >= 44 ? "-0.015em" : "-0.012em",
      }}
      {...props}
    >
      <span aria-hidden="true">bl</span>
      <DoGlyph
        viewBox="2.75 1.85 10.5 11.75"
        width={w}
        height={h}
        className="inline-block overflow-visible"
        style={{ verticalAlign: valign, margin: `0 ${margin}px` }}
      />
      <span aria-hidden="true">eprints</span>
    </span>
  )
}
